import {
  addDrawerListeners,
  basePathRewrite,
  cetgoriesCount,
  checkForGuid,
  checkIsFreeJobAvailable,
  commonPropsForSSR,
  getDrawerPopCount,
  getEBendPoints,
  getEJendPoints,
  getFreeJobViewQuota,
  getfullUrl,
  getHostValue,
  getRmcCategories,
  handleDrawerScroll,
  readCookie,
  readFeatureCookieByKey,
  response410,
  setFeatureCookieByKey,
} from '@components/common';
import companiesMapping from '@components/common/companiesMapping.json';
import {
  ApplyJobStatus,
  getScrollHeightDrawerExp,
} from '@components/common/constant';
import {
  addJobsCountTraits,
  jobSearchResultTrackEvent,
} from '@components/common/event-tracker';
import rmcCategoriesObj from '@components/common/rmcCategories.json';
import { cdnEXPTType, IJobCount } from '@components/shared.types';
import { JobDetailsPage, JobSearchSteps } from '@license-admin/boldjobs';
import { countryNameToISOCode } from '@utils/countryCodeMap';
import jobDetailsPageProps from '@utils/jobDetailsPage';
import jobSearchStepsProps from '@utils/jobSearchStepsProps';
import { processJobDetailsForExperiment } from '@utils/showDetails';
import {
  applyJob,
  getCompaniesHiringCount,
  getJobDetails,
  jobViewed,
  modifyJobDetails,
} from 'API/jobs';
import { apiCallList } from 'API/wlb/ApiClient';
import { getFreeRemoteJobsByCategory, getJobsByCategory } from 'API/wlb/jobs';
import { getENVPath, isProdEnv } from 'helper';
import {
  getGuidValue,
  getOrUpdateGUID,
  getSearchType,
} from 'helper/eventTrackingUtils';
import {
  Action,
  SearchType,
  USER_TRAITS_JOBS_COUNT,
  ViewType,
} from 'helper/eventTrackingUtils/constant';
import { getExperimentVariant } from 'helper/experiments/experimentActions';
import { RMCPortalExperiments } from 'helper/experiments/experimentConstants';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import ErrorPages from 'src/pages/_error';

// Interfaces
interface IProps {
  jobDetails: any;
  categoryBasedSchema?: boolean;
  similarJobCategoryName: string;
  url: string;
  host: string;
  isPremiumUser?: boolean;
  isLoggedIn?: boolean;
  themeType?: string;
  isFreeJob?: boolean;
  deviceType: string;
  userAgent: string;
  localization?: any;
  cdnExptData?: cdnEXPTType;
  category?: any;
  isSecondaryModalPopup?: boolean;
  hiring_count?: IJobCount;
  similarJobs?: any;
  userInteractExpId?: number;
}

// Helper Functions
const sanitizeDescription = (description: string) => {
  return description
    .replace(/"/g, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]*>?/gm, '');
};

const parseSalaryRange = (salaryRange: string) => {
  if (!salaryRange)
    return { minSalary: 0.0, maxSalary: null, salaryUnitText: '' };
  const range = salaryRange.split(' ') || [];
  if (range.length > 1) {
    const cleanedData = range
      .map((item) => item.replace(/-/g, '')) // Remove hyphens
      .filter((item) => item !== ''); // Remove empty strings

    // Extract numeric values, properly handling commas in numbers
    const minSalaryStr = cleanedData[0]?.replace(/,/g, '');
    const maxSalaryStr = cleanedData[1]?.replace(/,/g, '');

    const salaryUnitText = cleanedData[cleanedData.length - 1] || '';

    return {
      minSalary: minSalaryStr ? parseInt(minSalaryStr, 10) || 0 : 0,
      maxSalary: maxSalaryStr ? parseInt(maxSalaryStr, 10) : null,
      salaryUnitText: salaryUnitText,
    };
  }

  return { minSalary: 0.0, maxSalary: null, salaryUnitText: '' };
};

const extractCountryCode = (location: any): any => {
  if (!location) return 'US';

  if (Array.isArray(location)) {
    if (location.length === 0) return 'US';
    return extractCountryCode(location[0]);
  }
  if (typeof location === 'string' && location.length > 0) {
    const foundCountry = Object.keys(countryNameToISOCode).find((country) =>
      location.toLowerCase().includes(country.toLowerCase())
    );
    return foundCountry ? countryNameToISOCode[foundCountry] : 'US';
  }
  return 'US';
};
const createJobPostingObject = (
  jobDetails: any,
  host: string,
  salaryData: any
) => {
  const pattern = /US National|USA|United States/g;

  const QuantitativeValueObj =
    salaryData.maxSalary == null
      ? {
          '@type': 'QuantitativeValue',
          value: salaryData.minSalary,
          unitText: salaryData.salaryUnitText,
        }
      : {
          '@type': 'QuantitativeValue',
          minValue: salaryData.minSalary,
          maxValue: salaryData.maxSalary,
          unitText: salaryData.salaryUnitText,
        };
  const jobPostingObj: any = {
    '@type': 'JobPosting',
    title: jobDetails?.title || '',
    description: sanitizeDescription(jobDetails?.description || ''),
    datePosted: jobDetails?.postedDate,
    validThrough: jobDetails?.expireOn,
    applicantLocationRequirements: {
      '@type': 'Country',
      name: jobDetails?.jobLocations.map((text: any) =>
        text.replace(pattern, 'US')
      ),
    },
    employmentType: jobDetails?.jobSchedules[0] || '',
    hiringOrganization: {
      '@type': 'Organization',
      name: jobDetails?.company?.name || '',
      logo: `https://${host}${
        jobDetails?.company?.logo ||
        '/blobcontent/rmc/images/remote-co-logo.svg'
      }`,
    },
  };

  if (jobDetails?.remoteOptions[0]?.includes('100')) {
    jobPostingObj['jobLocationType'] = 'TELECOMMUTE';
  } else {
    jobPostingObj['jobLocation'] = {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: jobDetails?.cities[0]?.replace(pattern, 'US') || '',
        addressCountry: extractCountryCode(jobDetails?.countries) || '',
      },
    };
  }

  if (QuantitativeValueObj.unitText !== '') {
    jobPostingObj['baseSalary'] = {
      '@type': 'MonetaryAmount',
      currency: jobDetails?.salaryRange !== '' ? 'USD' : 0.0,
      value: QuantitativeValueObj,
    };
  }

  return jobPostingObj;
};

// Main Component
export default function JobDetails(props: IProps) {
  const router = useRouter();
  const {
    categoryBasedSchema,
    jobDetails,
    isPremiumUser,
    isLoggedIn,
    themeType,
    isFreeJob,
    localization,
    deviceType,
    host,
    isSecondaryModalPopup,
    hiring_count,
    similarJobs,
    similarJobCategoryName,
    userInteractExpId,
  } = props;
  const jobDetailItem = { getRmcCategories, ...jobDetails };
  const [showJobSearchSteps, setShowJobSearchSteps] = useState(false);
  const [savedJobsArray, setSavedJobsArray] = useState<number[]>([]);
  const [shouldShowSearchStepDrawer, setShouldShowSearchStepDrawer] = useState(
    !isLoggedIn
  );
  const [hiringCount, setHiringCount] = useState<number | undefined>();
  const [guids, setGuid] = useState<any>(getOrUpdateGUID()); // for event tracking
  const isMobileView = deviceType === 'mobile';
  useEffect(() => {
    if (isPremiumUser && isLoggedIn) {
      jobViewed(jobDetails.id);
      jobSearchResultTrackEvent(
        Action.Viewed,
        null,
        ViewType.Separate_Tab,
        null,
        jobDetails,
        null,
        null,
        null,
        null,
        null
      );
    }
  }, [jobDetails]);

  useEffect(() => {
    const popCount = getDrawerPopCount();
    if (shouldShowSearchStepDrawer && popCount < 5) {
      getCompaniesHiringCount().then(setHiringCount);
      addDrawerListeners(setShowJobSearchSteps, undefined, true);
    }

    return () => {
      window.removeEventListener('scroll', () =>
        handleDrawerScroll(getScrollHeightDrawerExp(), setShowJobSearchSteps)
      );
    };
  }, []);

  const updateSaveJobState = (jobId: number, status: boolean) => {
    setSavedJobsArray((prevState) =>
      status ? [...prevState, jobId] : prevState.filter((id) => id !== jobId)
    );
  };

  const salaryData = parseSalaryRange(jobDetails?.salaryRange || '');
  let jobPostingObj = createJobPostingObject(jobDetails, host, salaryData);
  let paywall_schema = {};
  if (categoryBasedSchema) {
    paywall_schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${jobDetails?.title} - Remote.co`,
      url: `https://${host}/job-details/${jobDetails?.slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Remote.co',
        url: `https://${host}`,
      },
      hasPart: {
        '@type': 'WebPageElement',
        isAccessibleForFree: 'False',
        cssSelector: '.job-details-paywall-wrapper',
      },
      isAccessibleForFree: 'False',
    };
  }

  if (!jobDetails) {
    return <ErrorPages statusCode={410} />;
  }

  const breadCrumbProps = {
    breadCrumbName: `${
      props.isPremiumUser ? 'public-jobs-page-premium' : 'public-jobs-page'
    }`,
    breadCrumbNameValue: [jobDetails?.title],
    breadCrumbsData: [
      {
        page: 'public-jobs-page',
        title: 'Public Jobs Page',
        children: [
          {
            name: 'Home',
            path: '/',
            static: false,
          },
          {
            name: 'Remote Jobs',
            path: '/remote-jobs',
            static: false,
          },
          {
            name: '{0}',
            path: '',
            static: true,
          },
        ],
      },
      {
        page: 'public-jobs-page-premium',
        title: 'Public Jobs Page',
        children: [
          {
            name: 'My Dashboard',
            path: '/dashboard',
            static: false,
          },
          {
            name: 'Remote Jobs',
            path: '/remote-jobs',
            static: false,
          },
          {
            name: '{0}',
            path: '',
            static: true,
          },
        ],
      },
    ],
  };
  const breadCrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://remote.co/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Remote Jobs',
        item: 'https://remote.co/remote-jobs',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: jobDetails?.title + ' - Remote.co',
        item: 'https://remote.co/job-details/' + jobDetails?.slug,
      },
    ],
  };
  const categories = rmcCategoriesObj?.find(
    (item: any) => item?.category?.slug === props?.category
  );
  const { jobTypes, jobSchedules, careerLevel } = props?.jobDetails ?? {};
  const { category } = props;
  const matchCategory = (list: any) =>
    list?.find((item: any) => item.toLowerCase() === category);

  const getJobTypes = matchCategory(jobTypes);
  const getJobSchedules = matchCategory(jobSchedules);
  const getCareerLevel = matchCategory(careerLevel);

  const breadcrumbJobDetailsCat =
    category?.toLowerCase() === getJobTypes?.toLowerCase() ||
    category?.toLowerCase() === getJobSchedules?.toLowerCase() ||
    category?.toLowerCase() === getCareerLevel?.toLowerCase()
      ? getJobTypes || getJobSchedules || getCareerLevel
      : categories;

  const breadcrumbJobDetailsCatName =
    categories?.category?.name ?? breadcrumbJobDetailsCat;
  const breadCrumbNameWithCategories = {
    ...categories,
    ...breadcrumbJobDetailsCat,
    breadcrumbJobDetailsCatName: breadcrumbJobDetailsCatName,
  };

  const handleApplyJob = () => {
    addJobsCountTraits(USER_TRAITS_JOBS_COUNT.JOB_APPLICATIONS);
    applyJob(jobDetails.id, ApplyJobStatus.applied);
    trackJobSearchResult(Action.Applied, jobDetails.id);
  };
  const fetchJobData = async (jobID: string) => {
    if (!jobID) throw new Error('Job ID is required for tracking.');

    const jobDetailResp = await getJobDetails(jobID);
    const jobDetails = jobDetailResp?.data;
    if (!jobDetails) throw new Error(`No job details found for ID: ${jobID}`);

    const guid = getGuidValue(setGuid);

    return { jobDetails, guid };
  };
  const determineAccessStatus = (jobDetails: any) => {
    if (isLoggedIn) {
      return isPremiumUser ? SearchType.Unlocked : SearchType.Locked;
    }
    return jobDetails?.isFreeJob ? SearchType.Unlocked : SearchType.Locked;
  };
  const trackJobSearchResult = async (
    action: string,
    jobID?: string,
    clickOption?: string
  ) => {
    const { jobDetails, guid } = await fetchJobData(jobID!);
    const accessStatus = determineAccessStatus(jobDetails);

    return jobSearchResultTrackEvent(
      action,
      getSearchType(),
      ViewType.Job_Post_Details,
      accessStatus,
      jobDetails,
      jobDetails,
      null,
      clickOption,
      null,
      guid
    );
  };
  const jobSearchStepsProp = jobSearchStepsProps({
    isCloseAllowed: true,
    isLoggedIn: isLoggedIn,
    themeType: themeType,
    isCategoryPage: false,
    signupAndFind: localization.unlock_banner.signup_and_find,
    jobsData: jobDetailItem,
    categoryDetails: {},
    hiringCount: props.hiring_count,
    localization: localization,
    screenName: 'jobs by category',
  });

  const jobDetailsPageProp = jobDetailsPageProps({
    jobDetails: jobDetailItem,
    isPremiumUser: isPremiumUser,
    isLoggedIn: isLoggedIn,
    themeType: themeType,
    isFreeJob: isFreeJob,
    deviceType: deviceType,
    breadCrumbProps: breadCrumbProps,
    isJobDetailsPage: true,
    handleBackBtn: (e: React.SyntheticEvent) => {
      e.preventDefault();
      router.back();
    },
    localization: localization,
    saveJobState: { savedJobsArray, updateSaveJobState },
    isExperiment: deviceType === 'mobile' && !props.isPremiumUser,
    breadCrumbNameWithCategories: breadCrumbNameWithCategories,
    basePathRewrite: basePathRewrite,
    isSecondaryModalPopup: isSecondaryModalPopup,
    handleApplyJob: handleApplyJob,
    trackJobSearchResult: trackJobSearchResult,
    cetgoriesCount: cetgoriesCount,
    hiring_count: hiring_count,
    similarJobs: similarJobs,
    similarJobCategoryName: similarJobCategoryName,
    userInteractExpId: userInteractExpId === 5,
  });

  return (
    <>
      <Head>
        {jobDetails.company && (
          <meta property="og:image" content={jobDetails.company.logo} />
        )}
        <title>{jobDetails.title} - Remote.co</title>
        <meta
          name="description"
          content={`Remote.co features 100% remote jobs. Check out this ${
            jobDetails.title
          } GPO job${
            jobDetails.company && jobDetails.company.name
              ? ' at ' + jobDetails.company.name
              : ''
          }.`}
        />
        <meta property="og:title" content={jobDetails.title} />
        <meta
          property="og:description"
          content={`Remote.co features 100% remote jobs. Check out this ${
            jobDetails.title
          } job${
            jobDetails.company && jobDetails.company.name
              ? ' at ' + jobDetails.company.name
              : ''
          }.`}
        />
        {isProdEnv && <link rel="canonical" href={props.url} />}
        <meta name="robots" content="max-image-preview:large, noodp" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={props.url} />
        <meta property="og:site_name" content="Remote.co" />
        <meta
          property="article:publisher"
          content="https://www.facebook.com/remote.co"
        />
        <meta
          property="article:modified_time"
          content="2024-11-13T02:06:00+00:00"
        />
        <meta property="og:image:width" content="100" />
        <meta property="og:image:height" content="100" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@remote_co" />
        <meta name="twitter:label1" content="Est. reading time" />
        <meta name="twitter:data1" content="4 minutes" />
        <meta name="isFreeJob" content={String(props?.isFreeJob || 'false')} />
        {!props?.isFreeJob && (
          <meta name="robots" content="noindex, nofollow" />
        )}{' '}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              {
                '@context': 'https://schema.org',
                '@graph': isMobileView
                  ? [jobPostingObj]
                  : [breadCrumbSchema, jobPostingObj],
              },
              null,
              isProdEnv ? 0 : 2
            ), // Pretty print JSON in development
          }}
        />
        {paywall_schema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(paywall_schema, null, isProdEnv ? 0 : 2),
            }}
          />
        )}
      </Head>
      <JobSearchSteps jobSearchStepsProps={jobSearchStepsProp} />
      <JobDetailsPage {...jobDetailsPageProp} />
    </>
  );
}

// Server-Side Props
export const getServerSideProps: GetServerSideProps<any> = async ({
  query,
  req,
  res,
}: GetServerSidePropsContext) => {
  if (!query.companyslug) {
    return { notFound: true };
  }

  const commonSSRProps = await commonPropsForSSR(req, 'report_job_loc');
  const { cookieHeader, deviceType, isPremiumUser } = commonSSRProps;

  let enableFreeJobsView = !isPremiumUser
    ? getENVPath().NEXT_FEATURE_FLAG_FREE_JOBS_VIEW
    : false;

  let companySlug = query.companyslug as string;
  if (companySlug === 'undefined') {
    return { notFound: true };
  }

  let jobId = checkForGuid(companySlug) || '';
  if (!jobId) {
    const lastNum = companySlug.substring(companySlug.lastIndexOf('-') + 1);
    if (!isNaN(parseInt(lastNum))) {
      jobId = lastNum;
    }
  }

  const apiEndpoints = {
    job_details: getEJendPoints().job_details,
    country_list: getEBendPoints().country_list,
    hiring_count: getEJendPoints().hiring_count,
  };
  if (apiEndpoints.job_details) {
    modifyJobDetails(apiEndpoints.job_details, jobId);
  }

  let userInteractExpId = '';
  if (deviceType === 'mobile') {
    userInteractExpId = RMCPortalExperiments.rmcUserInteractionMobile.id;
  } else {
    userInteractExpId = RMCPortalExperiments.rmcUserInteractionDesktop.id;
  }

  let expData = await getExperimentVariant(userInteractExpId, cookieHeader);

  let similarJobs = [];
  let similarJobCategoryName = '';
  let selectedCategory = readCookie('selectedCategory', cookieHeader);
  if (expData && expData == 5 && selectedCategory) {
    let categorizedJobs = await getJobsByCategory(
      { size: '8', cookieHeader, req },
      [selectedCategory]
    );

    if (
      categorizedJobs &&
      categorizedJobs[0] &&
      categorizedJobs[0].jobDetails
    ) {
      similarJobCategoryName = categorizedJobs[0].name;
      similarJobs = categorizedJobs[0].jobDetails.filter(
        (item: any) => item.id != jobId
      );
    }
  }

  const functionProps: any = await apiCallList({
    apiEndpoints,
    cookieHeader,
    req,
  }).then((data) => {
    const { isFreeJob = false, jobDetails = {} } = checkIsFreeJobAvailable(
      data.job_details
    );
    const hiring_count = data.hiring_count || {
      jobsCount: 0,
      companiesCount: 0,
    };
    return { isFreeJob, jobDetails, hiring_count };
  });

  /**
   *  Check The category id of the job.
   *  If the job is under category 1 or 4, then check the cookie for free jobs.
   *  Check allowed free jobs view limit is reached or not.
   *  If the limit is reached, then show locked job details page.
   *  If the limit is not reached, then show the job details page.
   *  And add this job under the category in the cookie.
   *  If the job is under free jobs category, then we will get the job details from the cookie.
   *  If the job is not under free jobs category, then we will get the job
   */

  let isLiesUnderFreeJobs = '';
  if (enableFreeJobsView) {
    const cookieVal =
      readFeatureCookieByKey('experimentBasedFreeRemoteJobs', cookieHeader) ||
      '';

    const cachedCategories = functionProps.jobDetails?.cachedInCategory;
    if (Array.isArray(cachedCategories)) {
      if (cachedCategories.includes('1')) {
        isLiesUnderFreeJobs = '1';
      } else if (cachedCategories.includes('4')) {
        isLiesUnderFreeJobs = '4';
      }
    }
    if (isLiesUnderFreeJobs) {
      let freeJobViewQuota;
      try {
        freeJobViewQuota = await getFreeJobViewQuota(
          String(isLiesUnderFreeJobs),
          commonSSRProps?.cookieHeader || req.headers.cookie
        );
      } catch (err) {
        // Could not get quota, treat as locked
        functionProps.jobDetails.isFreeJob = false;
        // functionProps.jobDetails.description = null;
      }

      const { allowViewFreeJob, viewedJobIds } = freeJobViewQuota || {};

      if (allowViewFreeJob) {
        let freeJobsObj: Record<string, string[]> = {};
        try {
          if (typeof cookieVal === 'string' && cookieVal !== '') {
            freeJobsObj = JSON.parse(cookieVal);
          } else if (typeof cookieVal === 'object' && cookieVal !== null) {
            freeJobsObj = cookieVal;
          }
        } catch {
          freeJobsObj = {};
        }
        const catId = String(isLiesUnderFreeJobs);
        freeJobsObj[catId] = freeJobsObj[catId] || [];
        if (!freeJobsObj[catId].includes(jobId)) {
          freeJobsObj[catId].push(jobId);
        }
        setFeatureCookieByKey({
          key: 'experimentBasedFreeRemoteJobs',
          value: JSON.stringify(freeJobsObj),
          cookieHeader: cookieHeader,
          res,
        });
      } else {
        let freeJobsByCategoryList = [];
        let foundValue: any = null;
        let foundKey: boolean = false;
        if (Array.isArray(viewedJobIds) && viewedJobIds.includes(jobId)) {
          foundKey = true;
        }
        if (foundKey) {
          try {
            freeJobsByCategoryList = await getFreeRemoteJobsByCategory(
              [isLiesUnderFreeJobs],
              cookieHeader
            );
            const categoryItem = freeJobsByCategoryList.find(
              (item: any) => item.key == isLiesUnderFreeJobs
            );
            if (categoryItem && Array.isArray(categoryItem.value)) {
              foundValue = categoryItem.value.find((i: any) => i.id == jobId);
            }
            if (foundValue) {
              functionProps.jobDetails = foundValue;
            } else {
              functionProps.jobDetails.isFreeJob = false;
              // functionProps.jobDetails.description = null;
            }
          } catch {
            functionProps.jobDetails.isFreeJob = false;
            // functionProps.jobDetails.description = null;
          }
        } else {
          functionProps.jobDetails.isFreeJob = false;
          // functionProps.jobDetails.description = null;
        }
      }
    }
  }

  if (!functionProps?.jobDetails?.title) {
    return response410(
      res,
      `Unable to get the title for job details, ${JSON.stringify(
        functionProps.jobDetails
      )}`
    );
  }
  if (functionProps?.jobDetails && functionProps?.jobDetails.company) {
    const companyMapping = companiesMapping.find(
      (company) => company.rmc === functionProps?.jobDetails.company.slug
    );
    if (!companyMapping) {
      functionProps.jobDetails.isrmcwpJob = true;
    }
  }

  functionProps.jobDetails = processJobDetailsForExperiment(
    functionProps.jobDetails,
    deviceType,
    cookieHeader,
    isPremiumUser,
    res
  );

  const { category } = query;
  return {
    props: {
      ...functionProps,
      ...commonSSRProps,
      similarJobs,
      categoryBasedSchema:
        isLiesUnderFreeJobs != '' && enableFreeJobsView ? true : false,
      similarJobCategoryName,
      userInteractExpId: expData,
      url: getfullUrl(req),
      host: getHostValue(req.headers.host || ''),
      userAgent: req.headers['user-agent'] || '',
      category: category || null,
      isSecondaryModalPopup: true,
      isSimilarJobs: similarJobs && similarJobs.length > 0,
    },
  };
};
